// Normalisation du champ `nutriments` d'Open Food Facts, qui est notoirement irrégulier : unités
// variables (kJ vs kcal, sel vs sodium), champs absents selon les produits, valeurs parfois
// sérialisées en chaîne de caractères plutôt qu'en nombre. Fonction pure, testée dans
// test/normalizeNutriments.test.ts (aucun appel réseau ici).
import type { MacrosPer100g } from './types.js';

type RawNutriments = Record<string, unknown>;

function round(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim().replace(',', '.');
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function firstNumber(raw: RawNutriments, keys: readonly string[]): number | null {
  for (const key of keys) {
    const value = toNumber(raw[key]);
    if (value !== null) return value;
  }
  return null;
}

/**
 * Réduit le `nutriments` brut d'une fiche Open Food Facts à une composition pour 100 g utilisable.
 * Renvoie `null` si les champs manquent pour produire une composition fiable — le point d'objectif 2
 * (« champs manquants fréquents ») exige de dégrader proprement, jamais de planter ni d'inventer une
 * valeur à zéro qui serait présentée comme une donnée exacte.
 */
export function normalizeNutriments(raw: unknown): MacrosPer100g | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as RawNutriments;

  const glucides = firstNumber(r, ['carbohydrates_100g']);
  const proteines = firstNumber(r, ['proteins_100g']);
  const lipides = firstNumber(r, ['fat_100g']);
  // Sans ces trois-là, OFF n'a rien de plus fiable à offrir qu'une estimation du modèle : autant
  // basculer sur le chemin modèle plutôt que d'afficher une table à moitié vide.
  if (glucides === null || proteines === null || lipides === null) return null;

  let kcal = firstNumber(r, ['energy-kcal_100g', 'energy-kcal_value']);
  if (kcal === null) {
    // `energy_100g` est en kJ dans le schéma OFF quand `energy-kcal_100g` est absent.
    const kilojoules = firstNumber(r, ['energy_100g', 'energy-kj_100g']);
    if (kilojoules !== null) kcal = kilojoules / 4.184;
  }
  if (kcal === null) return null;

  const fibres = firstNumber(r, ['fiber_100g']) ?? 0;
  const sucres = firstNumber(r, ['sugars_100g']) ?? 0;

  let sel = firstNumber(r, ['salt_100g']);
  if (sel === null) {
    const sodium = firstNumber(r, ['sodium_100g']);
    // Conversion standard sodium -> équivalent sel (facteur 2.5, utilisé par les étiquettes UE).
    if (sodium !== null) sel = sodium * 2.5;
  }

  return {
    kcal: round(kcal, 0),
    glucides_g: round(glucides, 2),
    proteines_g: round(proteines, 2),
    lipides_g: round(lipides, 2),
    fibres_g: round(fibres, 2),
    sucres_g: round(sucres, 2),
    sel_g: round(sel ?? 0, 3),
  };
}
